import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Save, Plus, Trash2 } from "lucide-react";

const DEFAULT_SITE = { brand: "Places", tagline: "The Real Estate Legacy", hashtag: "", whatsapp: "", whatsappLabel: "", email: "", units: [], socials: [] };

let _cuid = 0;
const withKey = (o) => ({ ...o, _k: o._k ?? `c${++_cuid}` });
const stripKey = ({ _k, ...rest }) => rest;

export default function ContactEditor() {
  const [site, setSite] = useState(DEFAULT_SITE);
  const [presence, setPresence] = useState({ active: [], past: [], international: [] });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/content/kv/site").then(({data}) => data && setSite({...DEFAULT_SITE, ...data, units: (data.units || []).map(withKey), socials: (data.socials || []).map(withKey)}));
    api.get("/content/kv/presence_extended").then(({data}) => data && setPresence(data));
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      const cleanSite = { ...site, units: site.units.map(stripKey), socials: site.socials.map(stripKey) };
      await api.put("/content/kv/site", { value: cleanSite });
      await api.put("/content/kv/presence_extended", { value: presence });
      toast.success("Contato e presença atualizados.");
    } catch { toast.error("Erro ao salvar."); }
    finally { setSaving(false); }
  };

  const updSite = (k, v) => setSite(s => ({...s, [k]: v}));
  const updUnit = (i, k, v) => setSite(s => { const arr = [...s.units]; arr[i] = {...arr[i], [k]: v}; return {...s, units: arr}; });
  const addUnit = () => setSite(s => ({...s, units: [...s.units, withKey({ city: "", state: "", role: "Unidade", phone: "", address: "" })]}));
  const removeUnit = (i) => setSite(s => ({...s, units: s.units.filter((_, j) => j !== i)}));
  const updSocial = (i, k, v) => setSite(s => { const arr = [...s.socials]; arr[i] = {...arr[i], [k]: v}; return {...s, socials: arr}; });
  const addSocial = () => setSite(s => ({...s, socials: [...s.socials, withKey({ name: "", url: "" })]}));
  const removeSocial = (i) => setSite(s => ({...s, socials: s.socials.filter((_, j) => j !== i)}));

  const presList = (key, label) => (
    <PresenceField
      value={presence[key] || []}
      onChange={(arr) => setPresence((p) => ({ ...p, [key]: arr }))}
      label={label}
      testid={`presence-${key}`}
    />
  );

  return (
    <div>
      <div className="flex justify-between items-end mb-6">
        <div><h1 className="font-display text-4xl">Contato & Presença</h1><p className="text-sm text-neutral-500 mt-1">Unidades, redes, WhatsApp, e onde já estivemos.</p></div>
        <button onClick={save} disabled={saving} data-testid="contact-save" className="bg-black text-white text-sm px-5 py-2.5 rounded-full hover:bg-accent-orange disabled:opacity-50"><Save className="w-3 h-3 inline mr-1" /> Salvar tudo</button>
      </div>

      <section className="mb-8 border border-black/10 p-5 rounded">
        <div className="font-semibold text-sm uppercase tracking-widest text-neutral-700 mb-3">Geral</div>
        <div className="grid md:grid-cols-2 gap-3">
          <div><label className="text-xs text-neutral-500">Marca</label><input value={site.brand} onChange={(e) => updSite("brand", e.target.value)} className="w-full border border-black/15 px-3 py-2 rounded text-sm" /></div>
          <div><label className="text-xs text-neutral-500">Tagline</label><input value={site.tagline} onChange={(e) => updSite("tagline", e.target.value)} className="w-full border border-black/15 px-3 py-2 rounded text-sm" /></div>
          <div><label className="text-xs text-neutral-500">Hashtag institucional</label><input value={site.hashtag} onChange={(e) => updSite("hashtag", e.target.value)} className="w-full border border-black/15 px-3 py-2 rounded text-sm" /></div>
          <div><label className="text-xs text-neutral-500">E-mail principal</label><input value={site.email} onChange={(e) => updSite("email", e.target.value)} className="w-full border border-black/15 px-3 py-2 rounded text-sm" /></div>
          <div><label className="text-xs text-neutral-500">WhatsApp (E.164 sem +)</label><input value={site.whatsapp} onChange={(e) => updSite("whatsapp", e.target.value)} placeholder="5562999999999" className="w-full border border-black/15 px-3 py-2 rounded text-sm font-mono" /></div>
          <div><label className="text-xs text-neutral-500">WhatsApp (exibido)</label><input value={site.whatsappLabel} onChange={(e) => updSite("whatsappLabel", e.target.value)} placeholder="(62) 9 9999-9999" className="w-full border border-black/15 px-3 py-2 rounded text-sm" /></div>
        </div>
      </section>

      <section className="mb-8 border border-black/10 p-5 rounded">
        <div className="flex justify-between items-center mb-3"><div className="font-semibold text-sm uppercase tracking-widest text-neutral-700">Unidades</div><button onClick={addUnit} className="text-xs bg-accent-yellow px-3 py-1 rounded-full font-semibold" data-testid="add-unit"><Plus className="w-3 h-3 inline" /> Adicionar</button></div>
        <div className="space-y-3">
          {site.units.map((u, i) => (
            <div key={u._k || `idx-${i}`} className="grid grid-cols-1 md:grid-cols-6 gap-2 items-end" data-testid={`unit-${i}`}>
              <input value={u.city} onChange={(e) => updUnit(i, "city", e.target.value)} placeholder="Cidade" className="border border-black/15 px-2 py-1.5 rounded text-sm" />
              <input value={u.state} onChange={(e) => updUnit(i, "state", e.target.value)} placeholder="UF" maxLength={2} className="border border-black/15 px-2 py-1.5 rounded text-sm uppercase" />
              <select value={u.role} onChange={(e) => updUnit(i, "role", e.target.value)} className="border border-black/15 px-2 py-1.5 rounded text-sm bg-white"><option>Matriz</option><option>Unidade</option><option>Filial</option></select>
              <input value={u.phone} onChange={(e) => updUnit(i, "phone", e.target.value)} placeholder="Telefone" className="border border-black/15 px-2 py-1.5 rounded text-sm" />
              <input value={u.address} onChange={(e) => updUnit(i, "address", e.target.value)} placeholder="Endereço" className="border border-black/15 px-2 py-1.5 rounded text-sm" />
              <button onClick={() => removeUnit(i)} className="text-red-500 text-xs px-2"><Trash2 className="w-3 h-3 inline" /></button>
            </div>
          ))}
        </div>
      </section>

      <section className="mb-8 border border-black/10 p-5 rounded">
        <div className="font-semibold text-sm uppercase tracking-widest text-neutral-700 mb-3">Áreas de atuação</div>
        <div className="grid md:grid-cols-3 gap-3">
          {presList("active", "Onde estamos (UFs)")}
          {presList("past", "Onde já fomos (UFs)")}
          {presList("international", "Internacional (países)")}
        </div>
      </section>

      <section className="border border-black/10 p-5 rounded">
        <div className="flex justify-between items-center mb-3"><div className="font-semibold text-sm uppercase tracking-widest text-neutral-700">Redes sociais</div><button onClick={addSocial} className="text-xs bg-accent-yellow px-3 py-1 rounded-full font-semibold" data-testid="add-social"><Plus className="w-3 h-3 inline" /> Adicionar</button></div>
        <div className="space-y-2">
          {site.socials.map((s, i) => (
            <div key={s._k || `idx-${i}`} className="grid grid-cols-12 gap-2 items-center" data-testid={`social-${i}`}>
              <input value={s.name} onChange={(e) => updSocial(i, "name", e.target.value)} placeholder="Instagram" className="col-span-3 border border-black/15 px-2 py-1.5 rounded text-sm" />
              <input value={s.url} onChange={(e) => updSocial(i, "url", e.target.value)} placeholder="https://instagram.com/agenciaplaces" className="col-span-8 border border-black/15 px-2 py-1.5 rounded text-sm" />
              <button onClick={() => removeSocial(i)} className="text-red-500 text-xs"><Trash2 className="w-3 h-3 inline" /></button>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}

/**
 * Textarea that stores plain text locally (so users can type commas + spaces
 * freely) and only parses to an array on blur or save. Fixes the bug where the
 * previous version re-derived `value` from `.split(",").filter(Boolean)` on
 * every keystroke, stripping trailing commas and spaces.
 */
function PresenceField({ value = [], onChange, label, testid }) {
  const [text, setText] = React.useState(value.join(", "));
  const externalKey = value.join("§"); // detect external resets (initial load)
  const lastKeyRef = React.useRef(externalKey);
  React.useEffect(() => {
    if (lastKeyRef.current !== externalKey) {
      // External change (e.g. initial hydration) — resync the text
      setText(value.join(", "));
      lastKeyRef.current = externalKey;
    }
  }, [externalKey, value]);

  const commit = (raw) => {
    const arr = raw.split(",").map((s) => s.trim()).filter(Boolean);
    lastKeyRef.current = arr.join("§");
    onChange(arr);
  };

  const parsedCount = text.split(",").map((s) => s.trim()).filter(Boolean).length;

  return (
    <div className="border border-black/10 p-4 rounded">
      <label className="text-xs text-neutral-500 mb-1 block">{label}</label>
      <textarea
        value={text}
        onChange={(e) => setText(e.target.value)}
        onBlur={(e) => commit(e.target.value)}
        rows={2}
        className="w-full border border-black/15 px-2 py-1.5 rounded text-sm"
        placeholder="Ex: SP, RJ, PR, SC"
        data-testid={testid}
      />
      <div className="text-[10px] text-neutral-400 mt-1">{parsedCount} itens · separe por vírgulas</div>
    </div>
  );
}
